import type { LocaleStrings } from "../types.ts";

export const enLocale: LocaleStrings = {
  game: {
    defaultClanName: "Your clan",
    clanSetupTitle: "Create your clan",
    clanSetupNameLabel: "Clan name",
    clanSetupNamePlaceholder: "Enter a name",
    clanSetupConfirm: "Confirm",
    settleConfirm: "Continue",
    promptConfirmStart: "Start here?",
    startTileConfirm: "Confirm",
    startTileCancel: "Cancel",
    clanSetupTotemLabel: "Totem",
    promptClanSetup: "Name your clan, choose a totem, and confirm to pick a starting tile.",
    statsHeaderFormat: "{clanName} · {turnLabel} {turn}",
    promptSelectStart: "Choose where your clan should start.",
    promptClanEstablished: "Clan established.",
    promptEndTurn: "End your turn when ready.",
    promptNewTurn: "Move to an adjacent tile or end your turn.",
    promptVictory: "Your clan has flourished!",
    promptDefeat: "Your clan has starved.",
    tileContentsFormat: "{terrain} — {food} food/turn",
    tileContentsReducedFormat: "{terrain} — {effective}/{base} food/turn",
    tileTravelingFormat: "{terrain} — traveling, no harvest this turn",
    turnLabel: "Turn",
    dayNightLabels: {
      day: "Day",
      dusk: "Dusk",
      night: "Night",
      dawn: "Dawn",
    },
    seasonLabels: {
      spring: "Spring",
      summer: "Summer",
      autumn: "Autumn",
      winter: "Winter",
    },
    temperatureFormat: "{value}°C",
    weatherLabels: {
      clear: "Clear",
      cloudy: "Cloudy",
      overcast: "Overcast",
      rainy: "Rainy",
      snowy: "Snowy",
    },
    populationLabel: "Population",
    foodLabel: "Food",
    waterLabel: "Water",
    ambientPredatorsLabel: "Predators",
    ambientPredatorLevelLabels: {
      none: "None",
      low: "Low",
      medium: "Medium",
      high: "High",
    },
    populationProgressFormat: "{current} / {max}",
    foodGrowthHintFormat: "→ growth at {threshold}",
    statsCollapsedChipFormat: "Turn {turn} · {population} · {food} · {water} · {predators}",
    endTurnLabel: "End Turn",
    terrainNames: {
      ["desert"]: "Desert",
      ["mountain"]: "Mountain",
      ["swamp"]: "Swamp",
      ["forest"]: "Forest",
      ["plains"]: "Plains",
      ["river"]: "River",
    },
    totemNames: {
      carved: "Carved",
      antler: "Antler",
      spiral: "Spiral",
    },
    victoryTitle: "Victory!",
    victoryMessage: "Your clan has grown to 30 people. A true civilization begins.",
    victoryRecapLineFormat: "{label}: {value}",
    victoryRecapTurns: "Turns to victory",
    victoryRecapPopulation: "Population",
    victoryRecapFood: "Food",
    victoryRecapStartTerrain: "Start terrain",
    victoryRecapEndTerrain: "End terrain",
    victoryRecapExplored: "Map explored",
    victoryRecapExploredFormat: "{explored} / {total} tiles",
    defeatTitle: "Defeat",
    defeatMessage: "The last of your people are gone.",
    startAgain: "Start again",
    resetLabel: "Reset",
    exitGame: "Exit game",
    settingsTitle: "Settings",
    settingsCloseAriaLabel: "Close settings",
    languageLabel: "Language",
    sfxVolumeLabel: "Sound FX — {level}",
    debugModeOff: "Debug Mode Off",
    debugModeOn: "Debug Mode On",
    fullscreenOff: "Fullscreen Off",
    fullscreenOn: "Fullscreen On",
    foodOverlayOff: "Show food on map",
    foodOverlayOn: "Hide food on map",
    waterOverlayOff: "Show water on map",
    waterOverlayOn: "Hide water on map",
    promptBannerHide: "Hide prompt banner",
    promptBannerShow: "Show prompt banner",
    stagingBadgeLabel: "Staging",
    hudSettingsTooltip: "Open settings",
    hudNotificationsTooltip: "Open notifications",
    notificationMarkDone: "Mark as done",
    notificationOk: "OK",
    notificationCancel: "Cancel",
    closeAriaLabel: "Close",
    leaveConfirmTitle: "Save and leave?",
    leaveConfirmBody:
      "Your progress will be saved. You can continue this game from the title screen.",
    leaveConfirmSave: "Save and leave",
    leaveConfirmStay: "Stay in game",
    leaveConfirmSaveFailed:
      "Could not save your game. Free some browser storage and try again.",
    notificationEmpty: "No notifications right now.",
    notificationPageOf: "{current} / {total}",
    notificationPageIndicatorOf: "({current} / {total})",
    notificationSwampTitle: "Marshland",
    notificationSwampBody: "Your clan has entered a swamp. The ground is soggy and progress feels slower here.",
    notificationFireTitle: "Fire discovered",
    notificationFireBody: "Your clan has found fire on this tile—a rare discovery as you explore the map.",
    notificationFoodDepletionTitle: "Land worn thin",
    notificationFoodDepletionBody:
      "Staying on the same tile lowers its food yield. Move on and let the land recover over time.",
    notificationTutorialTurnsTitle: "Your first turn",
    notificationTutorialTurnsBody:
      "Move to an adjacent tile, or end your turn when ready.",
    notificationTutorialStatsTitle: "Your clan stats",
    notificationTutorialStatsBody:
      "The panel in the top-left shows your turn, time of day, season, temperature, weather, population, food, water, and predators on your tile. Press E or tap End Turn when you're ready to gather food and advance.",
    notificationTutorialWinGoalTitle: "How to win",
    notificationTutorialWinGoalBody:
      "Grow your clan to 30 people to win. If your population falls to zero, the clan is lost.",
    notificationTutorialFoodEconomyTitle: "Food each turn",
    notificationTutorialFoodEconomyBody:
      "When you end your turn, your tile produces food. Your population eats first—the remainder is added to or taken from stored food.",
    notificationTutorialTileFoodTitle: "Reading a tile",
    notificationTutorialTileFoodBody:
      "Each revealed tile shows its terrain and food per turn. Explore before settling—fertile tiles such as plains and rivers feed your clan longer.",
    notificationTutorialGrowthTitle: "Population growth",
    notificationTutorialGrowthBody:
      "When stored food stays high enough for several turns in a row, your population grows by one.",
    notificationTutorialFamineTitle: "Going hungry",
    notificationTutorialFamineBody:
      "If your clan goes hungry for too many turns in a row, you lose one person. Move to fertile tiles and build reserves.",
    notificationTutorialOneMoveTitle: "Moving each turn",
    notificationTutorialOneMoveBody:
      "Press 1–6 or click one highlighted adjacent tile per turn, or stay put. Staying on the same tile too long wears the land out.",
    notificationTutorialFoodOverlayTitle: "Food on the map",
    notificationTutorialFoodOverlayBody:
      "Press F or use the grain button on the HUD to show food yields on revealed tiles without opening each tile.",
    notificationTutorialLandRecoveryTitle: "Land recovers",
    notificationTutorialLandRecoveryBody:
      "Tiles you leave slowly regain food over time. Keep moving so your clan does not drain one place.",
    notificationTutorialSettingsTitle: "Settings",
    notificationTutorialSettingsBody:
      "Press S or open the gear to change language, adjust sound effects, toggle fullscreen, restart the game, or return to the title screen.",
    notificationTutorialRandomEventsTitle: "Map events",
    notificationTutorialRandomEventsBody:
      "Unexpected events can appear as you explore. Press N or open the bell to read what happened and what it means.",
    languageLabels: {
      en: "English",
      it: "Italian",
      fr: "French",
      es: "Spanish",
      pt: "Portuguese",
      de: "German",
      zh: "Chinese",
      nl: "Dutch",
    },
  },
  site: {
    gameTitle: "Dawn of Clans",
    kofiButtonLabel: "Support me on Ko-fi",
    discordLinkLabel: "Join Discord",
    landing: {
      primaryDescription:
        "Dawn of Clans is a grand strategy game where players guide a prehistoric clan through survival, discovery, and societal evolution.",
      secondaryDescription:
        "Starting with a small nomadic group, you'll unlock technologies through experience, adapt to changing environments, and shape the emergence of the first civilizations.",
      earlyAccessLabel: "Early access",
      earlyAccessNotice:
        "In early development. The core survival loop is playable today; deeper strategy systems are coming.",
      learnMoreLabel: "Learn more",
      releaseNoticeText: "A new version has just been released!",
      playLabel: "Play",
      continueLabel: "Continue",
      savedGameSummary: "Turn {turn}, Population {population}",
      resumeModalTitle: "Saved game",
      newGameLabel: "New game",
      loadingLabel: "Loading...",
      changelogLink: "Changelog",
      careerTeaserFormat:
        "{wins} victories · fastest win: {fastest} turns · most explored: {explored}",
      careerViewChronicleLabel: "View win chronicle",
      chronicleModalTitle: "Win chronicle",
      chronicleModalCloseLabel: "Close",
      chronicleEntryFormat: "{clanName} · {turns} turns · {date}",
      chronicleClearWinHistoryLabel: "Clear win history",
      chronicleClearWinHistoryBody:
        "This removes all stored victories from this device. Your current game save is not affected.",
      chronicleClearWinHistoryConfirm: "Yes, clear history",
      chronicleClearWinHistoryCancel: "Cancel",
      settingsGearAriaLabel: "Settings",
      settingsCloseAriaLabel: "Close settings",
    },
    about: {
      pageTitle: "About Dawn of Clans",
      backLinkLabel: "← Back to Dawn of Clans",
      sections: [
        {
          heading: "Overview",
          body: "Dawn of Clans is a grand strategy game where players guide a prehistoric clan through survival, discovery, and societal evolution. Starting with a small nomadic group, you'll unlock technologies through experience, adapt to changing environments, and shape the emergence of the first civilizations.",
        },
        {
          heading: "Your clan",
          body: "Name your clan when a new game begins, then choose where to settle on the hex map. After you found your first settlement, track turn, population, and food in the stats panel. End each turn to feed your clan and decide whether to move to a neighbouring tile before the next turn.",
        },
        {
          heading: "Progression",
          body: "Each terrain produces a different amount of food each turn. When your stored food stays high enough for several turns in a row, your population grows; if your clan goes hungry for too long, people are lost. Build reserves on fertile tiles such as rivers and plains, then grow your clan to thirty people to win. If your population falls to zero, the clan is gone.",
        },
        {
          heading: "The world",
          body: "Explore a hexagonal map where tiles start hidden. Click to reveal desert, mountain, swamp, forest, plains, and river terrain. Choose where to establish your first settlement, then move to one adjacent tile per turn. Fertile land feeds more mouths; harsh land drains your food reserves quickly.",
        },
        {
          heading: "Status",
          body: "Dawn of Clans is in early development. Exploration, settlement, turn-based food and population, and win or lose outcomes are playable today. Deeper strategy systems are still being built.",
        },
      ],
      communitySection: {
        heading: "Community",
        body: "Chat with other players, share feedback, and follow development updates on our Discord server.",
      },
      supportSection: {
        heading: "Support the project",
        body: "Enjoying Dawn of Clans? Optional donations on Ko-fi help fund continued development.",
      },
    },
    changelog: {
      pageTitle: "Changelog",
      backLinkLabel: "← Back to Dawn of Clans",
      loadError: "Unable to load changelog.",
      emptyState: "No releases yet.",
    },
  },
};
